// apps/web/src/features/auth/components/LoginPage.tsx
// Spec §13.3: hydration-gated blank shell + post-hydration render.
//   - hydrating === true && !bootstrapThrottled → blank full-viewport
//     <div aria-hidden="true" minHeight:100vh> (no <LoadingState> — bootstrap
//     resolves <50ms; spinner flash is worse than blank)
//   - hydrating === true && bootstrapThrottled → <LoadingState /> (long-tail
//     retry pending after /auth/refresh 429 exhaustion; the wait is ≥60s, so
//     a visible+screen-reader-announced state replaces the silent blank)
//   - hydrating === false, user != null → navigate(landingForUser(user, perms)) once
//   - hydrating === false, user == null → <AuthLayout><LoginForm/></AuthLayout>
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../../../stores/auth.js';
import { landingForUser } from '../../../shell/menu.js';
import { LoadingState } from '../../../ui/index.js';
import { AuthLayout } from './AuthLayout.js';
import { LoginForm } from './LoginForm.js';

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const hydrating = useAuthStore((s) => s.hydrating);
  const bootstrapThrottled = useAuthStore((s) => s.bootstrapThrottled);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hydrating && user) navigate(landingForUser(user, permissions), { replace: true });
  }, [hydrating, user, permissions, navigate]);

  if (hydrating && bootstrapThrottled)
    return <LoadingState text={t('common:states.restoringSession')} />;
  if (hydrating) return <div aria-hidden="true" style={{ minHeight: '100vh' }} />;
  return (
    <AuthLayout
      title={t('auth:login.title')}
      subtitle={t('auth:login.subtitle')}
      footer={t('auth:login.footer')}
    >
      <LoginForm />
    </AuthLayout>
  );
}
